// Firebase service that works in both web and Electron environments
import isElectron from './isElectron.js';

class FirebaseService {
  constructor() {
    this.isElectron = isElectron();
  }

  // Get user settings - prefer Web SDK in both environments for offline support; fallback to IPC in Electron
  async getUserSettings(userEmail) {
    try {
      const { doc, getDoc, database } = await import('./firebaseConfig.js');
      const userSettingsDocRef = doc(database, "user-settings", userEmail);
      const userSettingsDoc = await getDoc(userSettingsDocRef);
      return userSettingsDoc.exists() ? userSettingsDoc.data() : null;
    } catch (err) {
      if (this.isElectron && window.electronAPI?.getUserSettings) {
        const result = await window.electronAPI.getUserSettings(userEmail);
        if (result.success) return result.data;
        throw new Error(result.error);
      }
      throw err;
    }
  }

  // Update user settings - works in both environments
  async updateUserSettings(userEmail, settings) {
    try {
      const { doc, updateDoc, database } = await import('./firebaseConfig.js');
      const userDocRef = doc(database, "user-settings", userEmail);
      await updateDoc(userDocRef, settings);
      return { success: true };
    } catch (err) {
      if (this.isElectron && window.electronAPI?.updateUserSettings) {
        const result = await window.electronAPI.updateUserSettings(userEmail, settings);
        if (!result.success) throw new Error(result.error);
        return result;
      }
      throw err;
    }
  }

  // Generic Firestore operations
  async getFirestoreData(collection, userId) {
    try {
      const { doc, getDoc, database } = await import('./firebaseConfig.js');
      const docRef = doc(database, collection, userId);
      const docSnap = await getDoc(docRef);
      return docSnap.exists() ? docSnap.data() : null;
    } catch (err) {
      if (this.isElectron && window.electronAPI?.getFirestoreData) {
        const result = await window.electronAPI.getFirestoreData(collection, userId);
        if (result.success) return result.data;
        throw new Error(result.error);
      }
      throw err;
    }
  }

  async setFirestoreData(collection, userId, data) {
    try {
      const { doc, setDoc, database } = await import('./firebaseConfig.js');
      const docRef = doc(database, collection, userId);
      await setDoc(docRef, data, { merge: true });
      return { success: true };
    } catch (err) {
      if (this.isElectron && window.electronAPI?.setFirestoreData) {
        const result = await window.electronAPI.setFirestoreData(collection, userId, data);
        if (!result.success) throw new Error(result.error);
        return result;
      }
      throw err;
    }
  }

  async updateFirestoreData(collection, userId, data) {
    try {
      const { doc, updateDoc, database } = await import('./firebaseConfig.js');
      const docRef = doc(database, collection, userId);
      await updateDoc(docRef, data);
      return { success: true };
    } catch (err) {
      if (this.isElectron && window.electronAPI?.updateFirestoreData) {
        const result = await window.electronAPI.updateFirestoreData(collection, userId, data);
        if (!result.success) throw new Error(result.error);
        return result;
      }
      throw err;
    }
  }
}

// Export a singleton instance
export const firebaseService = new FirebaseService(); 